import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { BoundEnabledPerspective, BoundPerspectiveAndTenant } from 'configuration-schemas/bindings.types';
import { SectionViewConf } from 'configuration-schemas/confdefnComponents';
import type { AppThunk } from 'src/store.types';
import type { AppState } from 'src/reducers';
import { RouteLink } from 'src/types/RouteLink';
import { extractNavPathFromString } from './navigationUtils';
import { isEnabledTab } from 'configuration-schemas/confdefn';
import { isNone } from 'fp-ts/lib/Option';

export interface NavigationShellSlice {
  /** Active Perspective */
  selected: BoundEnabledPerspective | null;
  activeTab: string;
  activePage: string;
  viewLinks: RouteLink[];
  /** For sections within a view that we want to hold navigation state for, such as opened accordions or tabs */
  activeSubPage?: string;
  activePageConf?: SectionViewConf;
  previousView?: string;
  /** one-shot redirect path set by epic on scopeExpired */
  redirectOnScopeExpired?: string | null;
}

const initialState: NavigationShellSlice = {
  selected: null,
  activeTab: '',
  activePage: '',
  viewLinks: [],
  redirectOnScopeExpired: null,
};

const navigationShellReducer = createSlice({
  name: 'NavigationShell',
  initialState: initialState as NavigationShellSlice,
  reducers: {
    resetNavigation: () => {
      return initialState;
    },
    setActivePerspective: (state, action) => {
      state.selected = action.payload;
    },
    // this action should be identical to `setActivePerspective`,
    // but is distinct in order to respond to epics in a slightly
    // different way
    hotSwitchActivePerspective: (
      state,
      action: PayloadAction<{
        selected: BoundPerspectiveAndTenant;
        viewLinks: RouteLink[];
      }>,
    ) => {
      if (action.payload.selected.perspective.type === 'Perspective') {
        state.selected = action.payload.selected.perspective;
      }
      state.viewLinks = action.payload.viewLinks;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setViewLinks: (state, action: PayloadAction<RouteLink[]>) => {
      state.viewLinks = action.payload;
    },
    preSetActivePage: (state) => state,
    // new combined version including setActivePageComponentPropsConf
    setActivePage: (state, action: PayloadAction<{ url: string; props?: SectionViewConf }>) => {
      state.activePage = action.payload.url;
      state.activePageConf = action.payload.props;
    },
    preSetActiveSubPage: (state) => state,
    setActiveSubPage: (state, action: PayloadAction<string | undefined>) => {
      state.activeSubPage = action.payload;
    },
    mfpModuleReady: (state) => state,
    setPreviousView: (state, action: PayloadAction<string>) => {
      state.previousView = action.payload;
    },
    setRedirectOnScopeExpired: (state, action: PayloadAction<string | null>) => {
      // state.activePage = action.payload;
      state.redirectOnScopeExpired = action.payload;
    },
  },
});

export const {
  resetNavigation,
  setActivePerspective,
  setActiveTab,
  preSetActivePage,
  setActivePage,
  setViewLinks,
  preSetActiveSubPage,
  setActiveSubPage,
  hotSwitchActivePerspective,
  mfpModuleReady,
  setPreviousView,
  setRedirectOnScopeExpired,
} = navigationShellReducer.actions;

// Thunk to set active page along with its component props
export const setActivePageAndComponentProps =
  (url: string): AppThunk =>
  (dispatch, getState: () => AppState) => {
    const navPath = extractNavPathFromString(url);
    if (isNone(navPath)) {
      dispatch(setActivePage({ url, props: undefined }));
      return;
    }

    const [, tabPath, sectionPath, viewPath] = navPath.value;

    const conf = getState().appConfig.tenantConfig;
    const tab = conf.tabs.filter(isEnabledTab).find((t) => t.pathSlot === tabPath);
    const section = tab?.leftNavSections?.find((s) => s.pathSlot === sectionPath);
    const maybeView: SectionViewConf | undefined = section?.views.find((v) => v.pathSlot === viewPath);

    dispatch(setActivePage({ url, props: maybeView }));
  };

export default navigationShellReducer.reducer;
